/**
 * RatingPromptHost (Expansion Phase G) — the hang-up rating prompt. Mounted
 * ONCE in CRMApp next to <SoftphoneBar /> (outside <Routes>, so it survives
 * navigation). A TOAST, never a modal: fixed bottom-20 right-4 (clear of the
 * softphone dock), z-40 — deliberately BELOW SoftphoneBar's z-50 so an
 * incoming call's Answer button always beats it. No backdrop, no focus steal.
 *
 * Tapping a star saves IMMEDIATELY (optimistic "Saved ✓"); a one-line note
 * revealed after the star PATCHes the same rating. Auto-dismisses after 30s
 * (restarted when a star is tapped, so a slow note isn't cut off);
 * startCall() clears lastCall so the prompt can never block the next call.
 */
import { FormEvent, useEffect, useState } from 'react';
import { Check, Loader2, Star, X } from 'lucide-react';
import api from '../../services/api';
import { useSoftphone } from '../contexts/SoftphoneContext';

const AUTO_DISMISS_MS = 30_000;

export default function RatingPromptHost() {
  const { lastCall, clearLastCall } = useSoftphone();

  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [savedRating, setSavedRating] = useState(0);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset per prompt appearance — a new lastCall replaces the prompt wholesale.
  useEffect(() => {
    setSaving(false);
    setSavedId(null);
    setSavedRating(0);
    setNote('');
    setNoteSaving(false);
    setError(null);
  }, [lastCall]);

  // Auto-dismiss; restarted when a star lands so the note isn't cut off.
  useEffect(() => {
    if (!lastCall) return;
    const t = window.setTimeout(clearLastCall, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [lastCall, savedId, clearLastCall]);

  if (!lastCall) return null;

  const rate = async (stars: number) => {
    if (saving || savedId) return;
    setSaving(true);
    setError(null);
    setSavedRating(stars); // optimistic
    try {
      const res = await api.post(`/api/crm/contacts/${lastCall.contactId}/ratings`, {
        rating: stars,
        ...(lastCall.callSid ? { call_sid: lastCall.callSid } : {}),
      });
      setSavedId(res.data?.rating?.id || null);
    } catch (e: any) {
      setSavedRating(0);
      setError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Could not save the rating');
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async (e: FormEvent) => {
    e.preventDefault();
    if (!savedId || noteSaving) return;
    if (!note.trim()) { clearLastCall(); return; }
    setNoteSaving(true);
    try {
      await api.patch(`/api/crm/ratings/${savedId}`, { note: note.trim() });
      clearLastCall();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Could not save the note');
      setNoteSaving(false);
    }
  };

  return (
    <div className="fixed bottom-20 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          How did the call with {lastCall.name || 'the patient'} go?
        </p>
        <button
          type="button"
          onClick={clearLastCall}
          aria-label="Skip rating"
          className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300 sm:min-h-0 sm:min-w-0 sm:p-0.5"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-2 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => rate(n)}
            disabled={saving || !!savedId}
            aria-label={`Rate ${n} of 5`}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-colors duration-150 hover:bg-amber-50 disabled:cursor-default dark:hover:bg-amber-900/20"
          >
            <Star
              className={`h-6 w-6 ${n <= savedRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
            />
          </button>
        ))}
        {saving && <Loader2 className="ml-1 h-4 w-4 animate-spin text-gray-400" />}
        {savedId && (
          <span className="ml-1 inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
            <Check className="h-3.5 w-3.5" /> Saved
          </span>
        )}
      </div>

      {error && <p role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>}

      {savedId ? (
        <form onSubmit={saveNote} className="mt-2 flex items-center gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            placeholder="How was the interaction? (no medical details)"
            aria-label="Rating note"
            className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:min-h-0"
          />
          <button
            type="submit"
            disabled={noteSaving}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {noteSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Done'}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={clearLastCall}
          className="mt-2 min-h-[44px] text-xs font-medium text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300 sm:min-h-0"
        >
          Skip
        </button>
      )}
    </div>
  );
}
